export const COCKPIT_SNAPSHOT_SCHEMA_VERSION = 1 as const;
export const COCKPIT_CONTROL_BASELINE_VERSION = '2026-08-12.1' as const;
export * from './cockpit-projections';
import type {
  CockpitArtifact,
  CockpitDeliveryProjection,
  CockpitGuaranteeProjection,
  CockpitSkillInvocation,
  CockpitSummaryProjection,
  CockpitTimeProjection,
  CockpitTimelineItem,
  CockpitUsageProjection,
} from './cockpit-projections';
import type { CockpitRevisionTrigger } from './control-plane-usage-reliability';
import type { SessionObservabilityProjection } from './session-observability';

export const COCKPIT_CONFIDENCE_VALUES = [
  'confirmed',
  'calculated',
  'estimated',
  'detected',
  'inferred',
  'partial',
  'unavailable',
  'load-failed',
] as const;

export const COCKPIT_COVERAGE_VALUES = ['complete', 'partial', 'none'] as const;

export const COCKPIT_LIFECYCLE_VALUES = [
  'running',
  'waiting',
  'completed',
  'interrupted',
  'unknown',
] as const;

export const COCKPIT_ARTIFACT_ROLE_VALUES = [
  'input',
  'working-document',
  'output',
] as const;

export const COCKPIT_ARTIFACT_ACCESS_VALUES = [
  'inspectable',
  'no-content',
  'forbidden',
] as const;

export const COCKPIT_RELATION_KIND_VALUES = [
  'used-as-input',
  'produced',
  'validated-by',
  'changed',
  'committed-as',
  'delivered-by',
  'supersedes',
] as const;

export const COCKPIT_SECTION_STATUS_VALUES = [
  'ready',
  'empty',
  'partial',
  'unavailable',
  'failed',
  'not-applicable',
] as const;

export const COCKPIT_PROVENANCE_VALUES = [
  'mcp-report',
  'agent-event',
  'artifact-store',
  'phase-conformity',
  'policy',
  'scm',
  'reported',
  'calculated',
  'legacy-metric',
  'cockpit-snapshot',
] as const;

export const COCKPIT_PUBLIC_ERROR_CODES = [
  'COCKPIT_INVALID_QUERY',
  'AUTH_REQUIRED',
  'ACTIVITY_READ_FORBIDDEN',
  'ACTIVITY_NOT_FOUND',
  'COCKPIT_REVISION_NOT_FOUND',
  'ARTIFACT_CONTENT_NOT_FOUND',
  'COCKPIT_WORK_PATH_MISMATCH',
  'COCKPIT_REVISION_NOT_AVAILABLE',
  'CONTROL_PLANE_HEALTH_INVALID',
  'COCKPIT_COMPOSITION_FAILED',
  'COCKPIT_SNAPSHOT_SCHEMA_UNSUPPORTED',
  'COCKPIT_INTEGER_OUT_OF_RANGE',
] as const;

export type CockpitConfidence = (typeof COCKPIT_CONFIDENCE_VALUES)[number];
export type CockpitCoverage = (typeof COCKPIT_COVERAGE_VALUES)[number];
export type CockpitLifecycle = (typeof COCKPIT_LIFECYCLE_VALUES)[number];
export type CockpitArtifactRole = (typeof COCKPIT_ARTIFACT_ROLE_VALUES)[number];
export type CockpitArtifactAccess =
  (typeof COCKPIT_ARTIFACT_ACCESS_VALUES)[number];
export type CockpitRelationKind = (typeof COCKPIT_RELATION_KIND_VALUES)[number];
export type CockpitSectionStatus =
  (typeof COCKPIT_SECTION_STATUS_VALUES)[number];
export type CockpitProvenance = (typeof COCKPIT_PROVENANCE_VALUES)[number];
export type CockpitPublicErrorCode =
  (typeof COCKPIT_PUBLIC_ERROR_CODES)[number];
export type CockpitWorkPath =
  | 'frontier-web'
  | 'cli-standalone'
  | 'control-plane';

export interface CockpitPublicError {
  code: CockpitPublicErrorCode;
  message: string;
  action: string | null;
}

export interface CockpitSection<T> {
  status: CockpitSectionStatus;
  value: T | null;
  confidence: CockpitConfidence;
  provenance: CockpitProvenance[];
  coverage: CockpitCoverage;
  updatedAt: string | null;
  limitations: string[];
  error: CockpitPublicError | null;
}

export interface CockpitNodeRef {
  kind: string;
  id: string;
  available?: boolean;
}

export interface CockpitRelation {
  id: string;
  from: CockpitNodeRef;
  to: CockpitNodeRef;
  kind: CockpitRelationKind;
  confidence: CockpitConfidence;
  provenance: CockpitProvenance;
  evidenceIds: string[];
}

export function unavailableCockpitNode(
  node: Omit<CockpitNodeRef, 'available'>,
): CockpitNodeRef {
  return { ...node, available: false };
}

export interface ActivityCockpitSections {
  summary: CockpitSection<CockpitSummaryProjection>;
  skills: CockpitSection<CockpitSkillInvocation[]>;
  artifacts: CockpitSection<CockpitArtifact[]>;
  guarantees: CockpitSection<CockpitGuaranteeProjection>;
  time: CockpitSection<CockpitTimeProjection>;
  usage: CockpitSection<CockpitUsageProjection>;
  delivery: CockpitSection<CockpitDeliveryProjection>;
  timeline: CockpitSection<CockpitTimelineItem[]>;
  relations: CockpitSection<CockpitRelation[]>;
}

export interface ActivityCockpitSnapshot {
  schemaVersion: typeof COCKPIT_SNAPSHOT_SCHEMA_VERSION;
  activityId: string;
  workPath: CockpitWorkPath;
  revision: number | null;
  generatedAt: string;
  sourceCutoffAt: string;
  historical: boolean;
  /** Optional for snapshots created before revision triggers were public. */
  revisionTrigger?: CockpitRevisionTrigger;
  /**
   * Optional live supplement resolved by the Control Plane Cockpit client.
   * Stored snapshots and other work paths remain wire-compatible without it.
   */
  sessionObservability?: {
    projection: SessionObservabilityProjection;
    stale: boolean;
  };
  sections: ActivityCockpitSections;
}

export interface CockpitBaselineControl {
  id:
    | 'identity-and-work-path'
    | 'skill-traceability'
    | 'artifact-provenance'
    | 'applicable-tests-and-gates'
    | 'delivery-link'
    | 'telemetry-coverage'
    | 'reconciled-usage';
  required: true;
  authority: 'orchestrated' | 'observed';
}

export interface CockpitControlBaseline {
  version: typeof COCKPIT_CONTROL_BASELINE_VERSION;
  workPath: CockpitWorkPath;
  controls: readonly CockpitBaselineControl[];
}

export interface ToCockpitSectionOptions {
  confidence?: CockpitConfidence;
  provenance?: CockpitProvenance[];
  coverage?: CockpitCoverage;
  updatedAt?: string | null;
  limitations?: string[];
}
